import { StrengthMeterCurrencyIndex } from '@/components/strength-meter/StrengthMeterCurrencyIndex';

// This class handles the strength meter calculations

export class StrengthCalculator {
  constructor(typeCalculator) {
    this.type = typeCalculator;

    // Class method binding
    this.initializeStrCalculator = this.initializeStrCalculator.bind(this);
    this.createListIndex = this.createListIndex.bind(this);
    this.calculateIndex = this.calculateIndex.bind(this);
    this.updateIndexesRates = this.updateIndexesRates.bind(this);
    this.updateIndexesStrengthLoop = this.updateIndexesStrengthLoop.bind(this);
    this.updateIndexesStrength = this.updateIndexesStrength.bind(this);
    this.calculateRate = this.calculateRate.bind(this);
    this.calculateStartRate = this.calculateStartRate.bind(this);
    this.createListSymbols = this.createListSymbols.bind(this);
    this.getPairChange = this.getPairChange.bind(this);
    this.getPairRate = this.getPairRate.bind(this);
    this.getInstrumentRate = this.getInstrumentRate.bind(this);
    this.getInstrumentChange = this.getInstrumentChange.bind(this);
    this.createStartRate = this.createStartRate.bind(this);
    this.getStringRating = this.getStringRating.bind(this);
  }

  initializeStrCalculator(instrumentsData, listCurrencies, timeframes) {
    this.timeframes = timeframes;
    this.listSymbols = this.createListSymbols(instrumentsData);
    this.listIndex = this.createListIndex(listCurrencies);
    this.updateIndexesStrengthLoop();
  }

  createListIndex(listCurrencies) {
    const result = [];
    listCurrencies.forEach((currency) => {
      const index = new StrengthMeterCurrencyIndex(
        currency,
        listCurrencies,
        this.type,
        this.listSymbols
      );
      this.calculateIndex(index, true);
      result.push(index);
    });
    return result;
  }
  calculateIndex(index, isInitial) {
    index.listPairs.forEach((pair) => {
      pair.rate = this.calculateRate(pair.base, pair.quote, 'rate');
      if (isInitial) {
        pair.startRate = this.calculateStartRate(
          pair.base,
          pair.quote,
          'startRate'
        );
      }
    });
    index.updateRating(this.timeframes);
  }
  updateIndexesRates(rawQuote) {
    const listQuotes = rawQuote.data;
    if (listQuotes) {
      //updating symbol rates
      listQuotes.forEach((quote) => {
        const quoteName = quote.s;
        const quoteRate = quote.p;
        /* eslint no-empty: ["error", { "allowEmptyCatch": true }] */
        try {
          this.listSymbols[quoteName].rate = quoteRate;
        } catch (err) {
          // console.log('strength calculator was not initialized');
        }
      });
      //updating pairs in all indexes
      /* eslint no-empty: ["error", { "allowEmptyCatch": true }] */
      try {
        this.listIndex.forEach((index) => {
          this.calculateIndex(index, false);
        });
      } catch (err) {
        // console.log('strength calculator was not initialized');
      }
    }
  }
  updateIndexesStrengthLoop() {
    for (const i in this.timeframes) {
      this.updateIndexesStrength(this.timeframes[i]);
    }
  }

  updateIndexesStrength(timeFrame) {
    const strongestRating = Math.max.apply(
      Math,
      this.listIndex.map(function (index) {
        return index.indexRating[timeFrame];
      })
    );
    const weakestRating = Math.min.apply(
      Math,
      this.listIndex.map(function (index) {
        return index.indexRating[timeFrame];
      })
    );
    const neutralRange = {
      from: weakestRating + strongestRating * 0.33,
      to: weakestRating + strongestRating * 0.66,
    };
    this.listIndex.forEach((index) => {
      index.stringRating[timeFrame] = this.getStringRating(
        index.indexRating[timeFrame],
        strongestRating,
        weakestRating,
        neutralRange
      );
    });
  }

  getStringRating(indexRating, strongestRating, weakestRating, neutralRange) {
    if (indexRating == strongestRating) {
      return 'Strongest';
    } else if (indexRating == weakestRating) {
      return 'Weakest';
    } else if (indexRating <= neutralRange.from) {
      return 'Weak';
    } else if (indexRating >= neutralRange.to) {
      return 'Strong';
    } else {
      return 'Neutral';
    }
  }

  calculateRate(base, quote, typeRate) {
    let result = 0;
    if (base == quote) {
      return 1;
    }
    for (const symbolName in this.listSymbols) {
      const symbol = this.listSymbols[symbolName];
      if (
        base.toLowerCase() == symbol.base.toLowerCase() &&
        quote.toLowerCase() == symbol.quote.toLowerCase()
      ) {
        result = symbol[typeRate];
        break;
      } else if (
        base.toLowerCase() == symbol.quote.toLowerCase() &&
        quote.toLowerCase() == symbol.base.toLowerCase()
      ) {
        result = 1 / symbol[typeRate];
        break;
      }
    }
    if (!result) {
      const baseUsdRate = this.calculateRate(base, 'USD', typeRate);
      const quoteUsdRate = this.calculateRate(quote, 'USD', typeRate);
      result = baseUsdRate / quoteUsdRate;
    }
    return result;
  }
  calculateStartRate(base, quote) {
    const result = {};
    if (base == quote) {
      for (const i in this.timeframes) {
        result[this.timeframes[i]] = 1;
      }
      return result;
    }
    for (const symbolName in this.listSymbols) {
      const symbol = this.listSymbols[symbolName];
      if (
        base.toLowerCase() == symbol.base.toLowerCase() &&
        quote.toLowerCase() == symbol.quote.toLowerCase()
      ) {
        for (const i in this.timeframes) {
          result[this.timeframes[i]] = symbol.startRate[this.timeframes[i]];
        }
        break;
      } else if (
        base.toLowerCase() == symbol.quote.toLowerCase() &&
        quote.toLowerCase() == symbol.base.toLowerCase()
      ) {
        for (const i in this.timeframes) {
          result[this.timeframes[i]] = 1 / symbol.startRate[this.timeframes[i]];
        }
        break;
      }
    }
    if (Object.keys(result).length == 0) {
      const baseUsdRate = this.calculateStartRate(base, 'usd');
      const quoteUsdRate = this.calculateStartRate(quote, 'usd');
      for (const i in this.timeframes) {
        result[this.timeframes[i]] =
          baseUsdRate[this.timeframes[i]] / quoteUsdRate[this.timeframes[i]];
      }
    }

    return result;
  }
  createListSymbols(instrumentsData) {
    const result = {};
    const listInstruments = Object.keys(instrumentsData);
    for (let i = 0; i < listInstruments.length; i++) {
      const instrument = listInstruments[i];
      const rate = instrumentsData[instrument].current_rate;
      const base = instrumentsData[instrument].base;
      const quote = instrumentsData[instrument].quote;
      const startRate = this.createStartRate(instrumentsData[instrument]);
      result[listInstruments[i]] = {
        base: base,
        quote: quote,
        rate: rate,
        startRate: startRate,
      };
    }
    return result;
  }

  createStartRate(instrumentData) {
    const result = {};
    for (const i in this.timeframes) {
      result[this.timeframes[i]] = instrumentData[this.timeframes[i]][0][1];
    }
    return result;
  }

  getPairChange(base, quote, timeFrame) {
    let result = 0;
    for (const i in this.listIndex) {
      const index = this.listIndex[i];
      for (const k in index.listPairs) {
        const pair = index.listPairs[k];
        if (pair.base == base && pair.quote == quote) {
          result =
            ((pair.rate - pair.startRate[timeFrame]) /
              pair.startRate[timeFrame]) *
            100;
          break;
        }
      }
    }
    return result;
  }
  getPairRate(base, quote) {
    let result = 0;
    for (const i in this.listIndex) {
      const index = this.listIndex[i];
      for (const k in index.listPairs) {
        const pair = index.listPairs[k];
        if (pair.base == base && pair.quote == quote) {
          result = pair.rate;
          break;
        }
      }
    }
    return result;
  }
  getInstrumentRate(pair) {
    const indexNeeded = this.listIndex.find((index) => {
      return index.indexName == pair;
    });
    return indexNeeded.listPairs[0].rate;
  }
  getInstrumentChange(pair, timeFrame) {
    const indexNeeded = this.listIndex.find((index) => {
      return index.indexName == pair;
    });
    const startRate = indexNeeded.listPairs[0].startRate[timeFrame];
    const rate = indexNeeded.listPairs[0].rate;
    return ((rate - startRate) / rate) * 100;
  }
}
